/**
 * Trigonometria determinística (doc 07, A2). `Math.sin`, `Math.cos` e `Math.atan2` não têm
 * arredondamento garantido pelo ECMAScript e podem diferir entre V8, SpiderMonkey e
 * JavaScriptCore. Estas versões usam apenas + − × ÷ e `Math.floor`/`Math.round`, cujo resultado
 * é idêntico em qualquer motor IEEE 754.
 *
 * Domínio garantido de `dsin`/`dcos`: |x| ≤ MAX_TRIG_ARG (erro < 1e-11). Fora dele a redução
 * de argumento perde precisão; o jogo só usa ângulos já normalizados ou fases de animação.
 */

export const PI = 3.141592653589793;
export const TAU = 6.283185307179586;
export const HALF_PI = 1.5707963267948966;
const QUARTER_PI = 0.7853981633974483;
/** π/2 em duas partes (Cody–Waite) para reduzir sem perder precisão. */
const HALF_PI_HI = 1.5707963267341256;
const HALF_PI_LO = 6.077100506506192e-11;
const INV_HALF_PI = 0.6366197723675814;
const TAN_PI_8 = 0.41421356237309503;
export const MAX_TRIG_ARG = 1e6;

// Séries de Taylor truncadas em |x| ≤ π/4: erro de truncamento < 1e-15.
function sinKernel(x: number): number {
  const x2 = x * x;
  return x * (1 + x2 * (-1 / 6 + x2 * (1 / 120 + x2 * (-1 / 5040 + x2 * (1 / 362880 + x2 * (-1 / 39916800 + x2 * (1 / 6227020800)))))));
}

function cosKernel(x: number): number {
  const x2 = x * x;
  return 1 + x2 * (-1 / 2 + x2 * (1 / 24 + x2 * (-1 / 720 + x2 * (1 / 40320 + x2 * (-1 / 3628800 + x2 * (1 / 479001600 + x2 * (-1 / 87178291200)))))));
}

/** Reduz `x` a `r ∈ [−π/4, π/4]` e quadrante `q ∈ {0,1,2,3}` com x ≈ q·π/2 + r. */
function reduce(x: number): { r: number; q: number } {
  const k = Math.round(x * INV_HALF_PI);
  const r = x - k * HALF_PI_HI - k * HALF_PI_LO;
  const q = ((k % 4) + 4) % 4;
  return { r, q };
}

export function dsin(x: number): number {
  if (!Number.isFinite(x)) return Number.NaN;
  const { r, q } = reduce(x);
  switch (q) {
    case 0:
      return sinKernel(r);
    case 1:
      return cosKernel(r);
    case 2:
      return -sinKernel(r);
    default:
      return -cosKernel(r);
  }
}

export function dcos(x: number): number {
  if (!Number.isFinite(x)) return Number.NaN;
  const { r, q } = reduce(x);
  switch (q) {
    case 0:
      return cosKernel(r);
    case 1:
      return -sinKernel(r);
    case 2:
      return -cosKernel(r);
    default:
      return sinKernel(r);
  }
}

/** atan em |t| ≤ tan(π/8) por série alternada: erro < 1e-9 com termos até t^19. */
function atanKernel(t: number): number {
  const t2 = t * t;
  let sum = 0;
  for (let n = 19; n >= 1; n -= 2) {
    sum = 1 / n - t2 * sum;
  }
  return t * sum;
}

export function datan(x: number): number {
  if (x !== x) return x;
  if (x === Infinity) return HALF_PI;
  if (x === -Infinity) return -HALF_PI;
  const sign = x < 0 ? -1 : 1;
  let a = x * sign;
  let offset = 0;
  if (a > 1) {
    // atan(a) = π/2 − atan(1/a)
    a = 1 / a;
    offset = HALF_PI;
  }
  let result: number;
  if (a > TAN_PI_8) {
    // atan(a) = π/4 + atan((a − 1)/(a + 1))
    result = QUARTER_PI + atanKernel((a - 1) / (a + 1));
  } else {
    result = atanKernel(a);
  }
  if (offset !== 0) result = offset - result;
  return sign * result;
}

/** Sinal com zero assinado: −0 conta como negativo. */
const isNegative = (v: number): boolean => v < 0 || Object.is(v, -0);

/** Mesma semântica de `Math.atan2`, inclusive zeros assinados, NaN e infinitos. */
export function datan2(y: number, x: number): number {
  if (y !== y || x !== x) return Number.NaN;
  const ySign = isNegative(y) ? -1 : 1;
  if (y === 0) {
    // atan2(±0, x): ±0 para x positivo ou +0; ±π para x negativo ou −0.
    return isNegative(x) ? ySign * PI : y;
  }
  if (x === 0) return ySign * HALF_PI;
  const yInf = !Number.isFinite(y);
  const xInf = !Number.isFinite(x);
  if (yInf && xInf) return ySign * (x > 0 ? QUARTER_PI : 3 * QUARTER_PI);
  if (yInf) return ySign * HALF_PI;
  if (xInf) return x > 0 ? ySign * 0 : ySign * PI;
  if (x > 0) return datan(y / x);
  return ySign * PI + datan(y / x);
}

/** Ângulo equivalente em (−π, π]. */
export function wrapAngle(angle: number): number {
  const wrapped = angle - TAU * Math.floor((angle + PI) / TAU);
  return wrapped <= -PI ? wrapped + TAU : wrapped;
}
